import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent } from "vue"
import _imports_0 from '@/assets/icons/arrow-left.svg'


const _hoisted_1 = {
  key: 0,
  class: "md:max-w-[656px] mb-8"
}
const _hoisted_2 = { class: "flex flex-col gap-8 md:gap-6 md:max-w-[312px]" }
const _hoisted_3 = {
  key: 0,
  class: "flex flex-col gap-8 md:gap-6 md:max-w-[312px]"
}
const _hoisted_4 = { class: "flex w-[640px] mt-12 items-start content-start gap-4 flex-wrap" }
const _hoisted_5 = { class: "flex items-center space-x-2" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "!mt-10 mb-10 md:!mt-12 md:!mb-12" }

import { onMounted, ref } from 'vue';
import { ISitecoreFormGenericInput } from '@/interfaces';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { getFormInput } from '@/utils/form-input-helper';
import PasswordInput from '@/components/PasswordInput/PasswordInput.vue';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import { v4 } from 'uuid';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { useAccountStore, useAnalyticsStore } from '@/store';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { useI18n } from 'vue-i18n';
import { FormEvents } from '@/enums/form-events.enum';
import Divider from '@/components/Divider/Divider.vue';
import { Link } from '@sitecore-jss/sitecore-jss-vue';

const failedToastMessage = 'Unable to save changes';


export default /*@__PURE__*/_defineComponent({
  __name: 'ChangePasswordPage',
  props: {
    content: {},
    id: {},
    name: {}
  },
  setup(__props: any) {

const getSetPasswordErrorMessage = (error: any): string => {
  const data = error?.response?.data;

  if (data && typeof data === 'object' && 'message' in data) {
    const apiMessage = data.message;
    if (typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
      return apiMessage;
    }
  } else if (typeof data === 'string' && data.trim().length > 0) {
    return data;
  }

  return failedToastMessage;
};

const accountStore = useAccountStore();
const toastAlertStore = useToastAlertStore();
const analyticsStore = useAnalyticsStore();

const props = __props;

const newPasswordRef = ref<any>(null);
const newPasswordKey = ref<string>('');
const confirmPasswordKey = ref<string>('');

const newPassword = ref(getFormInput(props.content, 'newPassword'));
const confirmPassword = ref(getFormInput(props.content, 'confirmPassword'));
const passwordNoMatchMessage = ref(getFormInput(props.content, 'passwordNoMatchMessage'));
const errorMessage = ref(getFormInput(props.content, 'errorMessage'));
const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));

const model = ref<any>({ currentPassword: '', newPassword: '' });

const formRef = ref<any>(null);
const showSaving = ref<boolean>(false);
const showPasswordMismatch = ref<boolean>(false);
const showApiErrorMessage = ref<boolean>(false);
const hasChanges = ref<boolean>(true); //default to true so button is enabled as per reqs

const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

const formData = ref({});

onMounted(() => {
  newPasswordKey.value = v4();
  confirmPasswordKey.value = v4();

  //Fixes styling issue when page content is short
  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.classList.add('grow-0');
  }
  pushAnalytics();
});

const onValidNewEmailEntered = (e: string) => {
  showPasswordMismatch.value = false;
  model.value.newPassword = e;
};

const onInvalidNewEmailEntered = (e: string) => {
  showPasswordMismatch.value = false;
};

const onSave = async () => {
  try {
    showApiErrorMessage.value = false;

    showPasswordMismatch.value = newPasswordRef.value?.password !== newPasswordRef.value?.password2;
    if (showPasswordMismatch.value) {
      return;
    }

    if (!accountStore.identityDetails?.email) {
      toastAlertStore.addToastAlert({
        title: failedToastMessage,
        description: '',
        type: ToastAlertType.ERROR,
        duration: 6000,
      });
      return;
    }

    showSaving.value = true;

    await accountStore
      .setPassword(model.value.newPassword, true)
      .then(async () => {
        toastAlertStore.addToastAlert({
          title: 'Password changed successfully',
          description: 'You will receive a confirmation email shortly.',
          type: ToastAlertType.SUCCESS,
          duration: 6000,
        });

        hasChanges.value = false;
        clearFields();

        showSaving.value = false;

        await analyticsStore.pushAnalytics({
          event: FormEvents.COMPLETE,
          form: {
            name: 'Change password',
            stepName: 'FormCompleted',
          },
        });
      })
      .catch(async (e) => {
        console.error(e);
        showSaving.value = false;
        const setPasswordErrorMessage = getSetPasswordErrorMessage(e);

        clearFields();
        toastAlertStore.addToastAlert({
          title: failedToastMessage,
          description: setPasswordErrorMessage,
          type: ToastAlertType.ERROR,
          duration: 6000,
        });

        showApiErrorMessage.value = setPasswordErrorMessage.toLowerCase().indexOf('password') > -1;

        await analyticsStore.pushAnalytics({
          event: FormEvents.JEOPARDY,
          form: {
            name: 'Change password',
            formErrorType: failedToastMessage,
            formErrorDetail: setPasswordErrorMessage,
          },
        });
      });
  } catch (error) {
    showSaving.value = false;
    const setPasswordErrorMessage = getSetPasswordErrorMessage(error);
    toastAlertStore.addToastAlert({
      title: failedToastMessage,
      description: setPasswordErrorMessage,
      type: ToastAlertType.ERROR,
      duration: 6000,
    });

    await analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Change password',
        formErrorType: failedToastMessage,
        formErrorDetail: setPasswordErrorMessage,
      },
    });
  }
};

const clearFields = () => {
  showApiErrorMessage.value = false;

  if (newPasswordRef.value?.password) {
    newPasswordRef.value.password = '';
  }

  if (newPasswordRef.value?.password2) {
    newPasswordRef.value.password2 = '';
  }

  if (formRef.value) {
    formRef.value?.node?.reset();
  }
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);
  pageAnalytics.pushAnalytics();
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createBlock(_component_FormKit, {
    id: "changePassword",
    ref_key: "formRef",
    ref: formRef,
    modelValue: formData.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData).value = $event)),
    name: "changePassword",
    type: "form",
    onSubmit: onSave
  }, {
    default: _withCtx(() => [
      _createVNode(FormStepInputs, {
        step: {
        stepName: _ctx.name,
        formInputs: _ctx.content,
      }
      }, {
        errorMessage: _withCtx(() => [
          (showApiErrorMessage.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(InformationBox, {
                  title: "",
                  class: "flex items-start gap-4 p-5 px-6 self-stretch",
                  description: errorMessage.value.content.value,
                  type: errorMessage.value.messageType.value,
                  isCircle: true,
                  useButtonSlot: false
                }, null, 8, ["description", "type"])
              ]))
            : _createCommentVNode("", true)
        ]),
        newPassword: _withCtx(() => [
          _createElementVNode("div", _hoisted_2, [
            (_openBlock(), _createBlock(PasswordInput, {
              ref_key: "newPasswordRef",
              ref: newPasswordRef,
              key: newPasswordKey.value,
              newPasswordLabel: newPassword.value.label.value,
              confirmPasswordLabel: confirmPassword.value.label.value,
              inputLabel: newPassword.value.label.value,
              showRequirements: true,
              onValid: _cache[0] || (_cache[0] = (e) => onValidNewEmailEntered(e)),
              onInvalid: _cache[1] || (_cache[1] = (e) => onInvalidNewEmailEntered(e))
            }, null, 8, ["newPasswordLabel", "confirmPasswordLabel", "inputLabel"]))
          ])
        ]),
        passwordNoMatchMessage: _withCtx(() => [
          (showPasswordMismatch.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(InformationBox, {
                  title: passwordNoMatchMessage.value.title.value,
                  description: passwordNoMatchMessage.value.content.value,
                  type: passwordNoMatchMessage.value.messageType.value,
                  isCircle: true,
                  useButtonSlot: false
                }, null, 8, ["title", "description", "type"])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["step"]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_unref(Link), {
          field: discardChangesLink.value?.link,
          class: "btn btn-tertiary btn-md"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_5, [
              _cache[3] || (_cache[3] = _createElementVNode("img", {
                src: _imports_0,
                alt: "left arrow",
                class: "w-5 h-5 inline-block"
              }, null, -1)),
              _createElementVNode("span", null, _toDisplayString(discardChangesLink.value?.link?.value?.text), 1)
            ])
          ]),
          _: 1
        }, 8, ["field"]),
        _createElementVNode("button", {
          type: "submit",
          disabled: !hasChanges.value,
          class: "btn btn-md btn-primary"
        }, _toDisplayString(_unref(getFormInput)(props.content, 'saveButtonText')?.label?.value ?? 'Change password'), 9, _hoisted_6)
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createVNode(Divider)
      ]),
      (showSaving.value)
        ? (_openBlock(), _createBlock(FullScreenSpinner, {
            key: 0,
            applyOpacity: true
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})